import React, {useState, useEffect} from 'react';
import {useNavigate} from 'react-router-dom';
import {
    AlertCircle,
    ArrowLeft,
    Bell, BookOpen,
    Check,
    CheckCircle,
    ChevronDown,
    ChevronUp,
    Clock,
    Home,
    Info,
    Trash2,
} from 'lucide-react';
import {API_CONSTANTS} from '../../Constants';

const UserNotifications = () => {
    const navigate = useNavigate();
    const [notifications, setNotifications] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);
    const [expandedNotification, setExpandedNotification] = useState(null);
    const [pagination, setPagination] = useState({
        page: 1,
        limit: 10,
        totalNotifications: 0,
        totalPages: 1,
    });

    // Fetch notifications from API
    useEffect(() => {
        const fetchNotifications = async () => {
            try {
                setIsLoading(true);
                setError(null);

                const token = localStorage.getItem('authToken');
                if (!token) {
                    throw new Error('Authentication required. Please log in.');
                }

                const queryParams = new URLSearchParams({
                    page: pagination.page,
                    limit: pagination.limit,
                    sortBy: 'date_created',
                    sortOrder: 'desc',
                });

                const response = await fetch(
                    `${API_CONSTANTS.BASE_URL}/dashboard/notifications?${queryParams}`,
                    {
                        method: 'GET',
                        headers: {
                            Authorization: `Bearer ${token}`,
                            'Content-Type': 'application/json',
                        },
                    }
                );

                const data = await response.json();
                if (!data.success) {
                    throw new Error(data.message || 'Failed to fetch notifications');
                }

                setNotifications(data.data.notifications);
                setPagination({
                    page: data.data.pagination.currentPage,
                    limit: data.data.pagination.limit,
                    totalNotifications: data.data.pagination.totalNotifications,
                    totalPages: data.data.pagination.totalPages,
                });
            } catch (err) {
                console.error('Error fetching notifications:', err);
                setError(err.message);
            } finally {
                setIsLoading(false);
            }
        };

        fetchNotifications();
    }, [pagination.page, pagination.limit]);

    // Toggle expanded state for a notification
    const toggleNotification = (id) => {
        setExpandedNotification(expandedNotification === id ? null : id);
    };

    // Mark a notification as read
    const markAsRead = async (id) => {
        try {
            const token = localStorage.getItem('authToken');
            const response = await fetch(
                `${API_CONSTANTS.BASE_URL}/dashboard/notifications/${id}/read`,
                {
                    method: 'POST',
                    headers: {
                        Authorization: `Bearer ${token}`,
                        'Content-Type': 'application/json',
                    },
                }
            );

            const data = await response.json();
            if (!data.success) {
                throw new Error(data.message || 'Failed to mark notification as read');
            }

            setNotifications(
                notifications.map((n) =>
                    n.id === id ? {...n, read: true, readAt: new Date().toISOString()} : n
                )
            );
        } catch (err) {
            console.error('Error marking notification as read:', err);
            setError(err.message);
        }
    };

// Delete a notification
    const removeNotification = async (id) => {
        try {
            const token = localStorage.getItem('authToken');
            const response = await fetch(
                `${API_CONSTANTS.BASE_URL}/dashboard/notifications/${id}`,
                {
                    method: 'DELETE',
                    headers: {
                        Authorization: `Bearer ${token}`,
                        'Content-Type': 'application/json',
                    },
                }
            );

            const data = await response.json();
            if (!data.success) {
                throw new Error(data.message || 'Failed to delete notification');
            }

            setNotifications(notifications.filter((n) => n.id !== id));
            setPagination((prev) => ({
                ...prev,
                totalNotifications: prev.totalNotifications - 1,
                totalPages: Math.ceil((prev.totalNotifications - 1) / prev.limit),
            }));
        } catch (err) {
            console.error('Error deleting notification:', err);
            setError(err.message);
        }
    };

// Clear all notifications
    const clearAllNotifications = async () => {
        try {
            const token = localStorage.getItem('authToken');
            const response = await fetch(
                `${API_CONSTANTS.BASE_URL}/dashboard/notifications/clear`,
                {
                    method: 'DELETE',
                    headers: {
                        Authorization: `Bearer ${token}`,
                        'Content-Type': 'application/json',
                    },
                }
            );

            const data = await response.json();
            if (!data.success) {
                throw new Error(data.message || 'Failed to clear notifications');
            }

            setNotifications([]);
            setPagination({
                ...pagination,
                totalNotifications: 0,
                totalPages: 0,
            });
        } catch (err) {
            console.error('Error clearing notifications:', err);
            setError(err.message);
        }
    };

// Get icon based on notification type
    const getNotificationIcon = (type) => {
        switch (type) {
            case 'success':
                return <CheckCircle className="w-6 h-6 text-highland-500"/>;
            case 'error':
                return <AlertCircle className="w-6 h-6 text-murram-500"/>;
            case 'info':
            default:
                return <Info className="w-6 h-6 text-jacaranda-500"/>;
        }
    };

// Format timestamp to readable date
    const formatTimestamp = (timestamp) => {
        return new Date(timestamp).toLocaleString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
        });
    };

    return (
        <div className="min-h-screen bg-karibu-100">
            <div style={{paddingTop: '5rem'}}></div>
            {/* Header */}
            <div className="relative bg-highland-700 text-karibu-100 overflow-hidden">
                <div
                    aria-hidden="true"
                    className="absolute top-0 left-1/2 -translate-x-1/2 w-[600px] h-[320px] rounded-arch bg-gradient-to-b from-dawn-500/20 via-highland-500/10 to-transparent blur-2xl"
                />
                <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
                    <div className="text-center">
                        <div className="flex items-center justify-center mb-4">
                            <div className="p-3 bg-white/10 rounded-full">
                                <Bell className="w-8 h-8"/>
                            </div>
                        </div>
                        <h1 className="font-display text-4xl font-bold mb-4">Notifications</h1>
                        <p className="text-xl text-karibu-200/80 max-w-3xl mx-auto">
                            Stay updated with important alerts, messages, and updates from Rent Utopia.
                        </p>
                    </div>
                </div>
            </div>

            {/* Navigation */}
            <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
                <div className="flex items-center space-x-2 text-sm text-soil-600">
                    <button
                        onClick={() => window.history.back()}
                        className="flex items-center hover:text-highland-500 transition-colors"
                    >
                        <ArrowLeft className="w-4 h-4 mr-1"/>
                        Back
                    </button>
                    <span>/</span>
                    <button
                        onClick={() => navigate('/')}
                        className="flex items-center hover:text-highland-500 transition-colors"
                    >
                        <Home className="w-4 h-4 mr-1"/>
                        Home
                    </button>
                    <span>/</span>
                    <span className="text-soil-900">Notifications</span>
                </div>
            </div>

            {/* Main Content */}
            <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 pb-12">
                {/* Actions */}
                <div className="bg-white rounded-xl shadow-sm p-6 mb-8 border border-soil-100">
                    <div className="flex justify-between items-center">
                        <h2 className="font-display text-xl font-semibold text-soil-900">Your Notifications</h2>
                        {notifications.length > 0 && (
                            <button
                                onClick={clearAllNotifications}
                                className="flex items-center text-sm text-murram-500 hover:text-murram-600 transition-colors"
                            >
                                <Trash2 className="w-4 h-4 mr-1"/>
                                Clear All
                            </button>
                        )}
                    </div>
                </div>

                {/* Notifications List */}
                <div className="space-y-4">
                    {isLoading ? (
                        <div className="bg-white rounded-xl shadow-sm p-8 text-center border border-soil-100">
                            <div className="w-12 h-12 mx-auto animate-spin text-highland-500 mb-4">
                                <Bell/>
                            </div>
                            <h3 className="text-lg font-semibold text-soil-900 mb-2">Loading Notifications</h3>
                            <p className="text-soil-600">Please wait while we fetch your notifications...</p>
                        </div>
                    ) : error ? (
                        <div className="bg-white rounded-xl shadow-sm p-8 text-center border border-soil-100">
                            <AlertCircle className="w-12 h-12 text-murram-500 mx-auto mb-4"/>
                            <h3 className="text-lg font-semibold text-soil-900 mb-2">Error</h3>
                            <p className="text-soil-600">{error}</p>
                        </div>
                    ) : notifications.length === 0 ? (
                        <div className="bg-white rounded-xl shadow-sm p-8 text-center border border-soil-100">
                            <Bell className="w-12 h-12 text-soil-400 mx-auto mb-4"/>
                            <h3 className="text-lg font-semibold text-soil-900 mb-2">No Notifications</h3>
                            <p className="text-soil-600">
                                You don't have any notifications at the moment. We'll let you know when there's
                                something new!
                            </p>
                        </div>
                    ) : (
                        notifications.map((notification) => {
                            const isExpanded = expandedNotification === notification.id;
                            return (
                                <div
                                    key={notification.id}
                                    className="border border-soil-100 rounded-lg overflow-hidden"
                                >
                                    <button
                                        onClick={() => toggleNotification(notification.id)}
                                        className="w-full px-6 py-4 text-left bg-white hover:bg-soil-50 transition-colors duration-200 flex items-center justify-between"
                                    >
                                        <div className="flex items-center space-x-3">
                                            {getNotificationIcon(notification.type)}
                                            <div>
                                                <h3 className="text-base font-semibold text-soil-900">
                                                    {notification.title || notification.message}
                                                </h3>
                                                <p className="text-sm text-soil-600 truncate max-w-md">
                                                    {notification.message}
                                                </p>
                                                <div className="flex items-center space-x-2 text-sm text-soil-400">
                                                    <Clock className="w-4 h-4"/>
                                                    <span>{formatTimestamp(notification.timestamp)}</span>
                                                </div>
                                            </div>
                                        </div>
                                        <div className="flex items-center space-x-3">
                                            {!notification.read && (
                                                <button
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        markAsRead(notification.id);
                                                    }}
                                                    className="text-highland-500 hover:text-highland-600 text-sm"
                                                >
                                                    <Check className="w-4 h-4 inline mr-1"/>
                                                    Mark as Read
                                                </button>
                                            )}
                                            <button
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    removeNotification(notification.id);
                                                }}
                                                className="text-murram-500 hover:text-murram-600 text-sm"
                                            >
                                                <Trash2 className="w-4 h-4 inline mr-1"/>
                                                Delete
                                            </button>
                                            {isExpanded ? (
                                                <ChevronUp className="w-5 h-5 text-soil-400"/>
                                            ) : (
                                                <ChevronDown className="w-5 h-5 text-soil-400"/>
                                            )}
                                        </div>
                                    </button>
                                    {isExpanded && (
                                        <div className="px-6 py-4 bg-soil-50 border-t border-soil-100">
                                            <p className="text-soil-600 text-sm">{notification.message}</p>
                                            <div className="mt-2 text-sm text-soil-400">
                                                <strong>Type:</strong>{' '}
                                                {notification.type.charAt(0).toUpperCase() + notification.type.slice(1)}
                                            </div>
                                            <div className="mt-1 text-sm text-soil-400">
                                                <strong>Received:</strong> {formatTimestamp(notification.timestamp)}
                                            </div>
                                            {notification.actionUrl && notification.actionText && (
                                                <div className="mt-3">
                                                    <button
                                                        onClick={() => navigate(notification.actionUrl)}
                                                        className="text-highland-500 hover:text-highland-600 text-sm font-medium"
                                                    >
                                                        {notification.actionText}
                                                    </button>
                                                </div>
                                            )}
                                        </div>
                                    )}
                                </div>
                            );
                        })
                    )}
                </div>

                {/* Pagination */}
                {notifications.length > 0 && (
                    <div className="mt-6 flex justify-between items-center">
                        <div className="text-sm text-soil-600">
                            Showing {(pagination.page - 1) * pagination.limit + 1}-
                            {Math.min(pagination.page * pagination.limit, pagination.totalNotifications)} of{' '}
                            {pagination.totalNotifications} notifications
                        </div>
                        <div className="flex space-x-2">
                            <button
                                onClick={() => setPagination({...pagination, page: pagination.page - 1})}
                                disabled={pagination.page === 1}
                                className={`px-3 py-2 text-sm font-medium rounded-md ${
                                    pagination.page === 1
                                        ? 'bg-soil-100 text-soil-400 cursor-not-allowed'
                                        : 'bg-highland-50 text-highland-500 hover:bg-highland-100'
                                }`}
                            >
                                Previous
                            </button>
                            <button
                                onClick={() => setPagination({...pagination, page: pagination.page + 1})}
                                disabled={pagination.page === pagination.totalPages}
                                className={`px-3 py-2 text-sm font-medium rounded-md ${
                                    pagination.page === pagination.totalPages
                                        ? 'bg-soil-100 text-soil-400 cursor-not-allowed'
                                        : 'bg-highland-50 text-highland-500 hover:bg-highland-100'
                                }`}
                            >
                                Next
                            </button>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default UserNotifications;
